import {
    AlertCircle,
    ChevronRight,
    DollarSign,
    Layers,
    MessageCircle,
    TrendingDown,
    TrendingUp,
    Users,
    Zap,
} from "lucide-react";
import { useFlowsList } from "../api/queries";
import { useDashboardStore } from "../store/dashboardStore";
import { PageHeader } from "../components/PageHeader";
import { PageShell } from "../components/PageShell";
import { SectionCard } from "../components/SectionCard";
import { KpiCard } from "../components/KpiCard";
import { BadgeHi } from "../components/BadgeHi";
import { DashSkeleton, SkelText } from "../components/Skeleton";
import { ApiBanner } from "../components/ApiBanner";
import { cur, deltaDir, fmt, fmtDelta } from "../lib/format";
import { cn } from "@/lib/utils";

export function FlowsView() {
    const setView = useDashboardStore((s) => s.setView);
    const { data, error, isLoading, refetch } = useFlowsList();
    const loading = isLoading && !data;

    const hero = (
        <PageHeader
            icon={MessageCircle}
            title="Flows"
            subtitle="Performance e drilldown por fluxo"
        />
    );

    if (error && !data) {
        return (
            <PageShell hero={hero}>
                <ApiBanner error={error} onRetry={() => refetch()} />
            </PageShell>
        );
    }

    const kpis = data?.kpis;
    const items = data?.items ?? [];

    // KPIs derivados quando o backend não manda direto.
    // Active/paused → conta `items[].status`. LeadsInFlow → soma activeLeads.
    const activeFlows =
        kpis?.activeFlows ??
        items.filter((f) => (f.status ?? "active") === "active").length;
    const pausedFlows =
        kpis?.pausedFlows ??
        items.filter((f) => f.status === "paused").length;
    const leadsInFlowFromKpi = cur(kpis?.leadsInFlow);
    const leadsInFlow =
        leadsInFlowFromKpi > 0
            ? leadsInFlowFromKpi
            : items.reduce((s, f) => s + (f.activeLeads ?? 0), 0);

    return (
        <PageShell hero={hero}>
            {/* 3 KPI cards (Receita / Flows ativos / Leads em flow) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KpiCard
                    icon={DollarSign}
                    iconColor="text-emerald-500"
                    label="Receita Flows"
                    value={
                        loading ? (
                            <SkelText />
                        ) : (
                            "$" +
                            Number(cur(kpis?.revenue)).toLocaleString("pt-BR", {
                                maximumFractionDigits: 2,
                            })
                        )
                    }
                    delta={fmtDelta(kpis?.revenue)}
                    deltaDir={deltaDir(kpis?.revenue)}
                />
                <KpiCard
                    icon={Zap}
                    iconColor="text-blue-500"
                    label="Flows ativos"
                    value={loading ? <SkelText /> : fmt(activeFlows)}
                    sub={
                        pausedFlows > 0
                            ? `${pausedFlows} pausado${pausedFlows > 1 ? "s" : ""}`
                            : undefined
                    }
                />
                <KpiCard
                    icon={Users}
                    iconColor="text-violet-500"
                    label="Leads em flow"
                    value={loading ? <SkelText /> : fmt(leadsInFlow)}
                    delta={fmtDelta(kpis?.leadsInFlow)}
                    deltaDir={deltaDir(kpis?.leadsInFlow)}
                />
            </div>

            {/* Tabela */}
            <SectionCard
                icon={Layers}
                title={`Todos os flows${items.length ? ` (${items.length})` : ""}`}
                subtitle="clique numa linha pra abrir o relatório do flow"
            >
                {loading ? (
                    <DashSkeleton h={300} />
                ) : items.length === 0 ? (
                    <EmptyMsg />
                ) : (
                    <div className="overflow-x-auto -mx-5">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[10px] uppercase font-semibold text-gray-500 tracking-wider border-b border-gray-200">
                                    <th className="text-left px-5 py-2 w-12">#</th>
                                    <th className="text-left px-5 py-2">Flow</th>
                                    <th className="text-center px-5 py-2 w-24">
                                        Status
                                    </th>
                                    <th className="text-right px-5 py-2 w-20">
                                        Nodes
                                    </th>
                                    <th className="text-right px-5 py-2 w-24">
                                        Envios
                                    </th>
                                    <th className="text-right px-5 py-2 w-28">
                                        Receita
                                    </th>
                                    <th className="text-right px-5 py-2 w-24">
                                        Vs ant.
                                    </th>
                                    <th className="text-right px-5 py-2 w-8" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {items.map((f, i) => {
                                    const status =
                                        (f.status ?? "active").toLowerCase();
                                    const isActive = status === "active";
                                    const deltaPct = f.deltaPct;
                                    const deltaDirRow: "up" | "down" | "flat" =
                                        deltaPct == null || deltaPct === 0
                                            ? "flat"
                                            : deltaPct > 0
                                              ? "up"
                                              : "down";
                                    return (
                                        <tr
                                            key={f.id}
                                            className="cursor-pointer hover:bg-gray-50 transition-colors"
                                            onClick={() =>
                                                setView(
                                                    { name: "flow", id: f.id },
                                                    f.name
                                                )
                                            }
                                        >
                                            <td className="px-5 py-3 text-xs text-gray-400 font-mono tabular-nums">
                                                {String(i + 1).padStart(2, "0")}
                                            </td>
                                            <td
                                                className="px-5 py-3 font-medium text-gray-800 max-w-md truncate"
                                                title={f.name}
                                            >
                                                {f.name}
                                            </td>
                                            <td className="px-5 py-3 text-center">
                                                <BadgeHi
                                                    color={
                                                        isActive
                                                            ? "emerald"
                                                            : "amber"
                                                    }
                                                >
                                                    {isActive
                                                        ? "Ativo"
                                                        : "Pausado"}
                                                </BadgeHi>
                                            </td>
                                            <td className="px-5 py-3 text-right text-gray-600 font-mono tabular-nums text-xs">
                                                {(() => {
                                                    const n =
                                                        f.nodes ?? f.nodesCount;
                                                    return n != null
                                                        ? fmt(n)
                                                        : "—";
                                                })()}
                                            </td>
                                            <td className="px-5 py-3 text-right text-gray-700 font-mono tabular-nums text-xs">
                                                {fmt(f.sends)}
                                            </td>
                                            <td className="px-5 py-3 text-right font-semibold text-gray-900 font-mono tabular-nums">
                                                $
                                                {Number(
                                                    f.revenue
                                                ).toLocaleString("pt-BR", {
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td
                                                className={cn(
                                                    "px-5 py-3 text-right font-mono tabular-nums text-xs",
                                                    deltaDirRow === "up" &&
                                                        "text-emerald-600",
                                                    deltaDirRow === "down" &&
                                                        "text-rose-600",
                                                    deltaDirRow === "flat" &&
                                                        "text-gray-400"
                                                )}
                                            >
                                                {deltaPct == null ? (
                                                    "—"
                                                ) : (
                                                    <span className="inline-flex items-center gap-0.5 justify-end">
                                                        {deltaDirRow ===
                                                            "up" && (
                                                            <TrendingUp className="w-3 h-3" />
                                                        )}
                                                        {deltaDirRow ===
                                                            "down" && (
                                                            <TrendingDown className="w-3 h-3" />
                                                        )}
                                                        {Math.abs(
                                                            deltaPct
                                                        ).toFixed(1)}
                                                        %
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-5 py-3 text-right">
                                                <ChevronRight className="w-4 h-4 text-gray-400 inline" />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </SectionCard>
        </PageShell>
    );
}

function EmptyMsg() {
    return (
        <div className="text-center py-12">
            <AlertCircle className="w-6 h-6 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Sem flows no período</p>
        </div>
    );
}
